import React, { useState, useEffect } from 'react';
import apiClient from 'apiClient';
import List from 'components/List/List';
import { Data, NetworksResponseData } from './types';
import { Row } from 'components/List/types';
import { RouteComponentProps } from 'react-router-dom';
import { networks as networksPaths, registerNetworkStep1 } from 'routes/routePaths';
import { getVerifyStrategyFactoryMethod } from 'utils/request/lists';
import useBodyClass from 'helpers/hooks/useBodyClass';

const mapResponseData = (data: any[]): NetworksResponseData =>
  data.map(
    (data: any): Data => ({
      fca_id: data.fca.id,
      fca_status: data.fca.is_active,
      fca_number: data.fca.number,
      network_location: data.city,
      network_email: data.email,
      network_name: data.name,
      id: data.id,
      lmc_status: data.is_active,
    }),
  );

const NetworksList: React.FC<RouteComponentProps> = ({ history }) => {
  const [networks, setNetworks] = useState<NetworksResponseData>([]);
  useBodyClass('light');

  useEffect(() => {
    let shouldCancel = false;

    const getNetworksList = async () => {
      const res = await apiClient.get('/api/v1/broker-management/networks/');
      if (!shouldCancel) setNetworks(mapResponseData(res.data));
    };

    getNetworksList();

    return () => {
      shouldCancel = true;
    };
  }, []);

  const prepareRows = (items: NetworksResponseData): Row[] => {
    return items.map((item) => ({
      key: item.id.toString(),
      data: [
        { label: item.network_name, link: { href: `${networksPaths.details}/${item.id}` } },
        { label: item.network_email },
        { label: item.fca_number },
        { label: item.network_location },
        { label: item.fca_status ? 'Active' : 'Inactive' },
        { label: item.lmc_status ? 'Active' : 'Inactive' },
        { label: item.fca_status ? 'Deactivate' : 'Activate', action: () => ({ item, status: 'fca_status' }) },
        { label: item.lmc_status ? 'Deactivate' : 'Activate', action: () => ({ item, status: 'lmc_status' }) },
      ],
    }));
  };

  const updateStatus = async ({ item, status }: { item: Data; status: 'fca_status' | 'lmc_status' }) => {
    try {
      const newNetworks = [...networks];
      const index = networks.findIndex((networkData) => networkData.id === item.id);
      const newStatus = !newNetworks[index][status];
      const verifyStrategy = getVerifyStrategyFactoryMethod('network', status)(item, newStatus);
      await apiClient.post(verifyStrategy.endpoint, verifyStrategy.body);

      if (index !== -1) {
        newNetworks[index][status] = newStatus;
        setNetworks(newNetworks);
      }
    } catch (err) {
      const message = err.message || err.response;
      console.log('Could not change status.'); // TO DO notify user of the error
      console.warn(message);
    }
  };

  const redirectToCreatePage = () => history.push(registerNetworkStep1);

  return (
    <List
      listName="Networks"
      labels={[
        'Network name',
        'Email',
        'FCA Number',
        'Location',
        'FCA Status',
        'LMC Status',
        'FCA Activation',
        'LMC Activation',
      ]}
      rows={prepareRows(networks)}
      onAction={updateStatus}
      addNewLabel="Add Network"
      onAddNewItem={redirectToCreatePage}
    />
  );
};

export default NetworksList;
